import { closestCorners, DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ValuesType } from '../../../interface/qr-generator.interface';
import MyButton from '../../../shared-components/button/Button';
import '../scss/qr-configuration.scss';

import DragAndDrop from './DragAndDrop';

interface QrConfigurationProperties {
  values: ValuesType[];
  setValues: React.Dispatch<React.SetStateAction<ValuesType[]>>;
  handleChange: (name: string, value: string | number | boolean) => void;
  serialNumbersToDownload: string[];
  deviceSNo?: string;
}

/**
 * QR Configuration Component
 * @param {QrConfigurationProperties} properties - Component properties
 * @param {ValuesType[]} properties.values - Configuration row values array
 * @param {React.Dispatch<React.SetStateAction<ValuesType[]>>} properties.setValues - State updater function for values
 * @param {(name: string, value: string | number | boolean) => void} properties.handleChange - Handler function for input changes
 * @param {string[]} properties.serialNumbersToDownload - Serial numbers list to download
 * @param {string} [properties.deviceSNo] - Single device serial number
 * @returns {JSX.Element} QR configuration component layout
 */
function QrConfiguration({
  values,
  setValues,
  handleChange,
  deviceSNo = '',
  serialNumbersToDownload,
}: QrConfigurationProperties): JSX.Element {
  const { t } = useTranslation('qrCode');

  /**
   * Getting Position from State
   * @param {number} id - Element ID to find index for
   * @returns {number} Index of element in values array
   */
  const getValuePos = (id: number): number => values.findIndex((value) => value.id === id);

  /**
   * Updating New Position In State after drag end
   * @param {any} event - Drag end event object from DndContext
   */
  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;
    setValues((previousValues) => {
      const originalPos = getValuePos(active.id);
      const newPos = getValuePos(over.id);

      return arrayMove(previousValues, originalPos, newPos);
    });
  };

  /**
   * Handling Add row Click Action
   */
  const handleClick = () => {
    const newConfig: ValuesType = {
      id: values.length + 1,
      key: `T${values.length - 1}`,
      text: t('sampleText'),
      display: true,
      fontFamily: 'Inter',
      fontSize: '16',
      editingText: false,
    };

    setValues((previous) => [...previous, newConfig]);
  };

  return (
    <div className="qr-config-wrap">
      <div className="qr-config-section">
        <div className="qr-config-creater">
          <div className="qr-config-adder">
            <p>{t('key')}</p>
            <p>{t('value')}</p>
            <MyButton
              label={t('add')}
              buttonType="submit"
              svgIcon={<PlusIcon />}
              onClickFunc={handleClick}
            />
          </div>
          <div className="bottom-line" />
          <div className="qr-config-dnd">
            <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCorners}>
              <SortableContext items={values} strategy={verticalListSortingStrategy}>
                <div>
                  {values?.map((value) => (
                    <DragAndDrop
                      key={value.id}
                      handleChange={handleChange}
                      values={value}
                      id={value.id}
                      serialNumbersToDownload={serialNumbersToDownload}
                      deviceSNo={deviceSNo}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>
    </div>
  );
}

QrConfiguration.defaultProps = {
  deviceSNo: '',
};

export default QrConfiguration;
